/**
 * Validation utilities for authentication forms
 */

/**
 * Validate phone number format (Chinese phone numbers)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export function validatePhone(phone) {
  if (!phone) return false
  const phoneRegex = /^1[3-9]\d{9}$/
  return phoneRegex.test(phone)
}

/**
 * Get phone validation error message
 * @returns {string} Error message
 */
export function getPhoneErrorMessage() {
  return '请输入有效的11位手机号码'
}

/**
 * Validate verification code format
 * @param {string} code - Verification code to validate
 * @returns {boolean} True if valid
 */
export function validateCode(code) {
  if (!code) return false
  const codeRegex = /^\d{6}$/
  return codeRegex.test(code)
}

/**
 * Get code validation error message
 * @returns {string} Error message
 */
export function getCodeErrorMessage() {
  return '请输入6位数字验证码'
}

/**
 * Validate password strength
 * Requirements: min 8 chars, uppercase, lowercase, number
 * @param {string} password - Password to validate
 * @returns {object} Object with isValid and errors array
 */
export function validatePassword(password) {
  const errors = []

  if (!password) {
    return {
      isValid: false,
      errors: ['密码不能为空']
    }
  }

  if (password.length < 8) {
    errors.push('密码至少需要8个字符')
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('密码必须包含大写字母')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('密码必须包含小写字母')
  }

  if (!/\d/.test(password)) {
    errors.push('密码必须包含数字')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate passwords match
 * @param {string} password - First password
 * @param {string} confirmPassword - Confirm password
 * @returns {boolean} True if they match
 */
export function validatePasswordMatch(password, confirmPassword) {
  return password === confirmPassword
}

/**
 * Get password match error message
 * @returns {string} Error message
 */
export function getPasswordMatchErrorMessage() {
  return '两次密码输入不一致'
}

/**
 * Check if login credentials are valid
 * @param {string} phone - Phone number
 * @param {string} password - Password
 * @returns {object} Object with isValid and errors array
 */
export function validateLoginCredentials(phone, password) {
  const errors = []

  if (!validatePhone(phone)) {
    errors.push(getPhoneErrorMessage())
  }

  if (!password) {
    errors.push('请输入密码')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Check if code login is valid
 * @param {string} phone - Phone number
 * @param {string} code - Verification code
 * @returns {object} Object with isValid and errors array
 */
export function validateCodeLogin(phone, code) {
  const errors = []

  if (!validatePhone(phone)) {
    errors.push(getPhoneErrorMessage())
  }

  if (!validateCode(code)) {
    errors.push(getCodeErrorMessage())
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

export default {
  validatePhone,
  getPhoneErrorMessage,
  validateCode,
  getCodeErrorMessage,
  validatePassword,
  validatePasswordMatch,
  getPasswordMatchErrorMessage,
  validateLoginCredentials,
  validateCodeLogin
}
